package ui.resource.editor


import androidx.compose.foundation.Canvas
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.graphics.drawscope.clipRect
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.IntSize
import androidx.compose.ui.unit.dp
import api.ResourceType
import api.VisualProperties
import compose.assets.Svg
import compose.assets.svg.Arrow_left_right
import compose.assets.svg.Aspect_ratio
import compose.assets.svg.Check
import io.Repository
import org.jetbrains.compose.resources.StringResource
import org.ssoft.common.type.ResolutionStandard
import ssoft.frontend.generated.resources.*
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState
import tools.rememberString
import ui.device.detail.editor.nameLong
import ui.widget.*
import kotlin.math.roundToInt

data class VisualEditorArgs(
    var companyId: Long = -1L,
    var resourceId: Long = -1,
    var upStack: Int = 2
)

@Composable
@ComposableNavNode(VisualEditorArgs::class)
fun VisualEditorUI() {
    with(VisualEditorScope.rememberScope()) {
        when (val state = vm.state) {
            is DataState.Error -> ErrUI()
            is DataState.Idle,
            is DataState.Loading -> LoadingUI()

            is DataState.Result -> Content(state.nnvl)
        }
    }
}

@Composable
fun VisualEditorScope.Content(nnvl: VisualEditorScope.Wrapper) {
    val confirmDialogState = DialogState.remember()
    ConfirmDialog(confirmDialogState, nnvl)
    val toolbarActions = remember {
        buildList {
            add(ToolbarAction(
                icon = Svg.Check,
                visible = nnvl.changed
            ) {
                confirmDialogState.show()
            })
        }
    }
    Column(Modifier.fillMaxSize()) {
        Toolbar(
            nav = nav,
            titlePrefix = nnvl.title.first,
            titleSuffix = nnvl.title.second,
            actions = toolbarActions,
        )
        val stateScroll = rememberScrollState()
        if (display.isMobile) {
            if (display.isLand()) {
                Row(Modifier.fillMaxSize()) {
                    PanelPreview(Modifier.fillMaxHeight().weight(1F), nnvl)
                    Box(
                        Modifier.width(512.dp)
                            .fillMaxHeight()
                            .verticalScroll(stateScroll)
                    ) {
                        Column {
                            PanelEditorPanel(
                                Modifier.fillMaxWidth()
                                    .weight(1F), nnvl
                            )
                            Space(64.dp)
                        }
                    }
                }
            } else {
                Box(
                    Modifier.fillMaxSize()
                        .verticalScroll(stateScroll)
                ) {
                    Column(Modifier.fillMaxWidth()) {
                        PanelPreview(Modifier.fillMaxWidth().height(256.dp), nnvl)
                        PanelEditorPanel(Modifier.fillMaxWidth(), nnvl)
                        Space(64.dp)
                    }
                }
            }

        } else {
            Row(Modifier.fillMaxSize()) {
                PanelPreview(Modifier.fillMaxHeight().weight(1F), nnvl)
                Box(
                    Modifier.fillMaxHeight()
                        .width(512.dp)
                        .verticalScroll(stateScroll)
                ) {
                    PanelEditorPanel(Modifier.fillMaxWidth(), nnvl)
                }
            }
        }
    }
}

@Composable
fun VisualEditorScope.ConfirmDialog(state: DialogState, nnvl: VisualEditorScope.Wrapper) {
    Dialog(state) {
        DialogUI(Modifier.align(Alignment.Center),
            onDismissRequest = {
                state.hide()
            },
            title = {
                Text(Res.string.visual_editor_confirm_dialog_title.rememberString())
            },
            dismissButton = {
                TextButton({ state.hide() }) {
                    Text(Res.string.dialog__action_cancel.rememberString())
                }
            },
            confirmButton = {
                TextButton({
                    action(VisualEditorVM.Action.Generate)
                    state.hide()
                }) {
                    Text(Res.string.dialog__action_generate.rememberString())
                }
            }) {
            Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
                Text(Res.string.visual_editor_confirm_dialog_message.rememberString())
                if (nnvl.canUserDeleteResource) {
                    if (nnvl.containsReferences) {
                        Text(
                            text = Res.string.visual_editor_confirm_dialog_delete_origin_block_msg.rememberString(),
                            style = MaterialTheme.typography.bodySmall,
                        )
                    } else {
                        Surface(
                            enabled = !nnvl.containsReferences,
                            shape = CardDefaults.shape,
                            onClick = { nnvl.propDelOrigin.set(!nnvl.propDelOrigin.state) }) {
                            Row(horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                                Checkbox(
                                    checked = nnvl.propDelOrigin.state,
                                    enabled = !nnvl.containsReferences,
                                    onCheckedChange = { nnvl.propDelOrigin.set(it) })
                                Text(
                                    text = Res.string.visual_editor_confirm_dialog_delete_origin.rememberString(),
                                    modifier = Modifier.align(Alignment.CenterVertically)
                                        .width(256.dp)
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
fun VisualEditorScope.PanelPreview(modifier: Modifier, nnvl: VisualEditorScope.Wrapper) {
    var image by remember {
        mutableStateOf<ImageBitmap?>(null)
    }
    val scaleFactor = remember {
        if (display.isMobile) 0.95F
        else 0.8F
    }
    Canvas(modifier.drawAlphaBackground()) {
        image?.also {
            val outMaxW = size.width * scaleFactor
            val outMaxH = size.height * scaleFactor
            var outSize = Size(
                width = outMaxW,
                height = outMaxW * (nnvl.propH.state.toFloat() / nnvl.propW.state.toFloat())
            )

            if (outMaxH < outSize.height) {
                outSize = Size(
                    width = outMaxH * (nnvl.propW.state.toFloat() / nnvl.propH.state.toFloat()),
                    height = outMaxH
                )
            }

            val diffOrigin = nnvl.data.width.toFloat() / nnvl.data.height.toFloat()
            val diffNew = nnvl.propW.state.toFloat() / nnvl.propH.state.toFloat()

            val offset = Offset(
                ((size.width - outSize.width) / 2F),
                ((size.height - outSize.height) / 2F)
            )
            val offsetInt = IntOffset(
                offset.x.roundToInt(),
                offset.y.roundToInt()
            )

            drawRect(
                color = Color.Black,
                topLeft = offset,
                size = outSize
            )
            when (nnvl.propS.state) {
                VisualProperties.SCALE_FILL -> {
                    drawImage(
                        image = it,
                        dstOffset = offsetInt,
                        dstSize = IntSize(outSize.width.roundToInt(), outSize.height.roundToInt()),
                    )
                }

                VisualProperties.SCALE_FIT -> {
                    when {
                        diffOrigin == diffNew -> {
                            drawImage(
                                image = it,
                                dstOffset = offsetInt,
                                dstSize = IntSize(outSize.width.roundToInt(), outSize.height.roundToInt()),
                            )
                        }

                        diffNew < diffOrigin -> { // center  height
                            val realSize = IntSize(
                                width = outSize.width.roundToInt(),
                                height = (outSize.width / diffOrigin).roundToInt()
                            )

                            drawImage(
                                image = it,
                                dstOffset = offsetInt + IntOffset(
                                    x = 0,
                                    y = ((outSize.height - realSize.height) / 2F).roundToInt()
                                ),
                                dstSize = realSize,
                            )
                        }

                        else -> { // center  width
                            val realSize = IntSize(
                                width = (outSize.height * diffOrigin).roundToInt(),
                                height = outSize.height.roundToInt()
                            )
                            drawImage(
                                image = it,
                                dstOffset = offsetInt + IntOffset(
                                    x = ((outSize.width - realSize.width) / 2F).roundToInt(),
                                    y = 0
                                ),
                                dstSize = realSize,
                            )
                        }
                    }
                }

                VisualProperties.SCALE_CROP_CENTER -> {
                    when {
                        diffOrigin == diffNew -> {
                            drawImage(
                                image = it,
                                dstOffset = offsetInt,
                                dstSize = IntSize(outSize.width.roundToInt(), outSize.height.roundToInt()),
                            )
                        }

                        diffNew < diffOrigin -> { // crop  width
                            val realSize = IntSize(
                                width = (outSize.height * diffOrigin).roundToInt(),
                                height = (outSize.height).roundToInt()
                            )
                            clipRect(
                                left = offset.x,
                                top = offset.y,
                                right = offset.x + outSize.width,
                                bottom = offset.y + outSize.height
                            ) {
                                drawImage(
                                    image = it,
                                    dstOffset = offsetInt - IntOffset(
                                        x = ((realSize.width.toFloat() - outSize.width) / 2F).roundToInt(),
                                        y = 0
                                    ),
                                    dstSize = realSize,
                                )
                            }
                        }

                        else -> { // crop  height
                            val realSize = IntSize(
                                width = (outSize.width).roundToInt(),
                                height = (outSize.width / diffOrigin).roundToInt()
                            )
                            clipRect(
                                left = offset.x,
                                top = offset.y,
                                right = offset.x + outSize.width,
                                bottom = offset.y + outSize.height
                            ) {
                                drawImage(
                                    image = it,
                                    dstOffset = offsetInt - IntOffset(
                                        x = 0,
                                        y = ((realSize.height.toFloat() - outSize.height) / 2F).roundToInt()
                                    ),
                                    dstSize = realSize,
                                )
                            }
                        }
                    }
                }
            }
        }
    }

    LaunchedEffect(Unit) {
        image = Repository.files.fetchImage(nnvl.data.id)
    }
}

@Composable
fun VisualEditorScope.PanelEditorPanel(modifier: Modifier, nnvl: VisualEditorScope.Wrapper) {
    Column(
        modifier = modifier.padding(16.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        EditorName(nnvl)
        EditorResolution(nnvl)
        EditorScale(nnvl)
        if (nnvl.type == ResourceType.VIDEO) {
            EditorDuration(nnvl)
        }
    }
}


@Composable
private fun EditorName(nnvl: VisualEditorScope.Wrapper) {
    EditorWrapper(Res.string.visual_editor_name) {
        TextField(
            isError = nnvl.propName.err,
            value = nnvl.propName.text,
            onValueChange = {
                nnvl.propName.set(it)
            },
            keyboardOptions = KeyboardOptions(
                autoCorrectEnabled = false,
                keyboardType = KeyboardType.Text,
                imeAction = ImeAction.Done
            ),
            modifier = Modifier.weight(1F)
        )
    }
}

@Composable
private fun EditorDuration(nnvl: VisualEditorScope.Wrapper) {
    EditorWrapper(Res.string.visual_editor_duration_title) {
        TextField(
            label = { Text(Res.string.visual_editor_duration_label.rememberString()) },
            isError = nnvl.propDur.err,
            value = nnvl.propDur.text,
            singleLine = true,
            onValueChange = {
                nnvl.propDur.set(it)
            },
            keyboardOptions = KeyboardOptions(
                autoCorrectEnabled = false,
                keyboardType = KeyboardType.Number,
                imeAction = ImeAction.Done
            ),
            supportingText = {
                Text(Res.string.visual_editor_duration_desc.rememberString())
            },
            modifier = Modifier.weight(1F)
        )
    }
}

@Composable
private fun EditorScale(nnvl: VisualEditorScope.Wrapper) {
    EditorWrapper(Res.string.visual_editor_transform_title) {
        Column(verticalArrangement = Arrangement.spacedBy(2.dp)) {
            VisualProperties.scaleVariants.forEach { variant ->
                Row(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(8.dp))
                        .clickable { nnvl.propS.set(variant) }
                        .padding(4.dp)
                ) {
                    RadioButton(
                        selected = nnvl.propS.state == variant,
                        modifier = Modifier.align(Alignment.CenterVertically),
                        onClick = { nnvl.propS.set(variant) })
                    Column(
                        verticalArrangement = Arrangement.spacedBy(2.dp),
                        modifier = Modifier.align(Alignment.CenterVertically)
                    ) {
                        Text(scaleVariantTitle(variant).rememberString())
                        Text(
                            text = scaleVariantSubtitle(variant).rememberString(),
                            style = MaterialTheme.typography.labelSmall
                        )
                    }
                }
            }
        }

    }
}


@Composable
private fun EditorResolution(nnvl: VisualEditorScope.Wrapper) {
    val focusW = remember {
        FocusRequester()
    }
    val focusH = remember {
        FocusRequester()
    }
    EditorWrapper(Res.string.visual_editor_resolution_title) {
        TextField(
            label = { Text(Res.string.visual_editor_resolution_width.rememberString()) },
            isError = nnvl.propW.err,
            value = nnvl.propW.text,
            singleLine = true,
            onValueChange = {
                nnvl.propW.set(it)
            },
            keyboardOptions = KeyboardOptions(
                autoCorrectEnabled = false,
                keyboardType = KeyboardType.Number,
                imeAction = ImeAction.Next
            ),
            keyboardActions = KeyboardActions {
                focusH.requestFocus()
            },
            modifier = Modifier.weight(1F)
                .focusRequester(focusW)
        )

        SmallFloatingActionButton(
            onClick = { nnvl.switchOrientation() },
            modifier = Modifier.align(Alignment.CenterVertically)
        ) {
            Icon(Svg.Arrow_left_right, null)
        }

        TextField(
            label = { Text(Res.string.visual_editor_resolution_height.rememberString()) },
            isError = nnvl.propH.err,
            value = nnvl.propH.text,
            singleLine = true,
            keyboardOptions = KeyboardOptions(
                autoCorrectEnabled = false,
                keyboardType = KeyboardType.Number,
                imeAction = ImeAction.Done
            ),
            onValueChange = {
                nnvl.propH.set(it)
            },
            modifier = Modifier.weight(1F)
                .focusRequester(focusH)
        )
        Box {
            var isMenuExpanded by remember {
                mutableStateOf(false)
            }
            FloatingActionButton({ isMenuExpanded = true }) {
                Icon(Svg.Aspect_ratio, null)
            }

            DropdownMenu(
                expanded = isMenuExpanded,
                onDismissRequest = { isMenuExpanded = false }) {
                (ResolutionStandard.all - ResolutionStandard.UNDEFINED).forEach {
                    DropdownMenuItem(
                        text = { Text(it.nameLong.rememberString()) },
                        onClick = {
                            isMenuExpanded = false
                            nnvl.setResolution(it)
                        }
                    )
                }
            }
        }
    }

}

@Composable
private fun EditorWrapper(title: StringResource, body: @Composable RowScope.() -> Unit) {
    Card(
        modifier = Modifier.fillMaxWidth(),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceContainer
        )
    ) {
        Column(Modifier.padding(16.dp)) {
            Text(
                text = title.rememberString(),
                style = MaterialTheme.typography.titleSmall
            )
            Space(4.dp)
            Row(horizontalArrangement = Arrangement.spacedBy(16.dp)) {
                body()
            }
        }
    }
}

private fun scaleVariantTitle(variant: Int): StringResource {
    return when (variant) {
        VisualProperties.SCALE_FIT -> Res.string.visual_editor_transform_fit_title
        VisualProperties.SCALE_FILL -> Res.string.visual_editor_transform_fill_title
        VisualProperties.SCALE_CROP_CENTER -> Res.string.visual_editor_transform_crop_title
        else -> Res.string.global_empty
    }
}

private fun scaleVariantSubtitle(variant: Int): StringResource {
    return when (variant) {
        VisualProperties.SCALE_FIT -> Res.string.visual_editor_transform_fit_subtitle
        VisualProperties.SCALE_FILL -> Res.string.visual_editor_transform_fill_subtitle
        VisualProperties.SCALE_CROP_CENTER -> Res.string.visual_editor_transform_crop_subtitle
        else -> Res.string.global_empty
    }
}
